import { SortOrder } from "../../stores/slices/columnSortSlice";

export type SortEntry = Record<string, unknown>;

export type ColumnSortOptions = {
  dateFields: Set<string>;
  /**
   * Fields where a missing value sorts last in *both* directions, rather than
   * being coerced to 0 and clustering at one end. Use it for columns most rows
   * leave blank — otherwise descending buries every row that has a value.
   */
  emptyLastFields?: Set<string>;
  numericFields: Set<string>;
};

const isEmptyValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  value === "" ||
  (typeof value === "number" && Number.isNaN(value));

/**
 * The comparator behind useColumnSortFunc, kept free of React so it can be
 * exercised on its own.
 */
export const createColumnSortFunc = (
  sort: { field: string; order: SortOrder },
  { dateFields, emptyLastFields, numericFields }: ColumnSortOptions,
): ((a: SortEntry, b: SortEntry) => number) => {
  return (a: SortEntry, b: SortEntry): number => {
    const fieldA = a[sort.field];
    const fieldB = b[sort.field];

    if (emptyLastFields?.has(sort.field)) {
      const emptyA = isEmptyValue(fieldA);
      const emptyB = isEmptyValue(fieldB);

      if (emptyA || emptyB) {
        if (emptyA && emptyB) return 0;
        return emptyA ? 1 : -1;
      }
    }

    if (numericFields.has(sort.field)) {
      if (sort.order === "asc") {
        return Number(fieldA) - Number(fieldB);
      } else {
        return Number(fieldB) - Number(fieldA);
      }
    }

    if (dateFields.has(sort.field)) {
      const dateA = new Date(String(fieldA));
      const dateB = new Date(String(fieldB));

      if (sort.order === "asc") {
        return dateA.getTime() - dateB.getTime();
      } else {
        return dateB.getTime() - dateA.getTime();
      }
    }

    if (sort.order === "asc") {
      return String(fieldA).localeCompare(String(fieldB));
    }
    return String(fieldB).localeCompare(String(fieldA));
  };
};
